// `baseUrl` is used for all public API calls and any links exposed to
// end users, including share URLs and SEO tags.
// `adminBaseUrl` is reserved for CMS and admin interfaces only.
import { FaRegCreditCard } from 'react-icons/fa';
import mastercardLogo from '../img/MastercardLogo.svg';
import paypalLogo from '../img/PaypalLogo.svg';
import visaLogo from '../img/VisaLogo.svg';
import alipayLogo from '../img/alipay.svg';
import amexLogo from '../img/amex.svg';
import eloLogo from '../img/elo.svg';
import hiperLogo from '../img/hiper.svg';
import hipercardLogo from '../img/hipercard.svg';
import jcbLogo from '../img/jcb.svg';
import maestroLogo from '../img/maestro.svg';
import mirLogo from '../img/mir.svg';
import unionpayLogo from '../img/unionpay.svg';
import { getEnv } from '../lib/env';

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

const resolveBrowserReachableEndpoint = (configuredUrl, { websocket = false } = {}) => {
  if (
    !configuredUrl ||
    typeof window === 'undefined' ||
    !window.location?.hostname ||
    LOOPBACK_HOSTNAMES.has(window.location.hostname)
  ) {
    return configuredUrl;
  }

  try {
    const parsed = new URL(configuredUrl);
    if (!LOOPBACK_HOSTNAMES.has(parsed.hostname)) {
      return configuredUrl;
    }

    if (websocket) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const path = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '/cable';
      return `${protocol}//${window.location.host}${path}${parsed.search}`;
    }

    // A phone cannot reach the development API through "localhost". Reuse the
    // page origin so Vite can proxy /api requests to Rails on the development Mac.
    return window.location.origin;
  } catch {
    return configuredUrl;
  }
};

const define_env = () => {
  const apiUrl = getEnv('REACT_APP_API_URL') || getEnv('VITE_API_URL');
  if (apiUrl) {
    return resolveBrowserReachableEndpoint(apiUrl);
  }

  const apiBase = getEnv('REACT_APP_API_BASE') || getEnv('VITE_API_BASE');
  if (apiBase) {
    return resolveBrowserReachableEndpoint(apiBase);
  }

  switch (getEnv('REACT_APP_ENV') || getEnv('VITE_ENV')) {
    case 'production':
      return 'https://api.nettgrav.com';
    case 'local':
      return 'http://localhost:3001';
    case 'staging':
      return 'https://stage-api.nettgrav.no';
    default:
      return 'http://localhost:3001';
  }
};

const define_admin_env = () => {
  const adminBase = getEnv('REACT_APP_ADMIN_BASE_URL') || getEnv('VITE_ADMIN_BASE_URL');
  if (adminBase) {
    return adminBase;
  }
  switch (getEnv('REACT_APP_ENV') || getEnv('VITE_ENV')) {
    case 'production':
      return 'https://admin.nettgrav.no';
    case 'local':
      return 'http://localhost:3001';
    case 'staging':
      return 'https://admin.stage.nettgrav.no';
    default:
      return 'http://localhost:3001';
  }
};

const define_media_env = () => {
  const mediaBase = getEnv('REACT_APP_MEDIA_BASE') || getEnv('VITE_MEDIA_BASE');
  if (mediaBase) {
    return mediaBase;
  }

  const apiBase = define_env();
  switch (getEnv('REACT_APP_ENV') || getEnv('VITE_ENV')) {
    case 'production':
      return 'https://media.nettgrav.com';
    case 'staging':
      return apiBase || 'https://stage-api.nettgrav.no';
    case 'local':
      return apiBase || 'http://localhost:3001';
    default:
      return apiBase || 'http://localhost:3001';
  }
};

const getEnvOverride = () =>
  getEnv('REACT_APP_ACTION_CABLE_URL') || getEnv('VITE_ACTION_CABLE_URL');

const normalizeWebsocketUrl = (rawUrl) => {
  if (!rawUrl) return rawUrl;

  let candidate = rawUrl;
  if (!/^\w+:\/\//.test(candidate)) {
    candidate = `https://${candidate.replace(/^\/+/, '')}`;
  }

  try {
    const parsed = new URL(candidate);
    if (!parsed.pathname || parsed.pathname === '/') {
      parsed.pathname = '/cable';
    }
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      parsed.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    }
    return parsed.toString();
  } catch (error) {
    console.warn('[ActionCable] Failed to normalise URL', candidate, error);
    return candidate;
  }
};

const resolveActionCableFromApiBase = () => {
  const apiBase = define_env();
  if (!apiBase) return null;
  return normalizeWebsocketUrl(`${apiBase.replace(/\/$/, '')}/cable`);
};

const setActionCableEndpoints = () => {
  const override = getEnvOverride();
  if (override) {
    return resolveBrowserReachableEndpoint(normalizeWebsocketUrl(override), {
      websocket: true,
    });
  }

  const resolvedFromApi = resolveActionCableFromApiBase();

  if (typeof window !== 'undefined' && window.location?.host) {
    const { protocol, hostname, port } = window.location;
    const isLocalHost = new Set(['localhost', '127.0.0.1', '0.0.0.0']).has(hostname);
    if (isLocalHost) {
      // In local development the frontend often runs on a Vite port while ActionCable
      // is mounted on the Rails API server. Always prefer the API-derived cable URL.
      return resolvedFromApi || 'ws://localhost:3001/cable';
    }
    const currentHost = port ? `${hostname}:${port}` : hostname;

    if (resolvedFromApi) {
      try {
        const parsed = new URL(resolvedFromApi);
        if (parsed.host && parsed.host !== currentHost) {
          return resolvedFromApi;
        }
      } catch {
        // ignore parse failure and fall back to location-derived URL
      }
    }

    const secure = protocol === 'https:';
    const scheme = secure ? 'wss' : 'ws';
    return `${scheme}://${currentHost}/cable`;
  }

  return resolvedFromApi || 'ws://localhost:3001/cable';
};
const CONSTANTS = {
  grave: {
    claimed: 'claimed',
    rejected: 'rejected',
  },
  actionCable: setActionCableEndpoints(),
  baseUrl: define_env(),
  adminBaseUrl: define_admin_env(),
  mediaBase: define_media_env(),
};

export { CONSTANTS };

export const HOST_LOCALE = {
  no: 'nettgrav.no',
  da: 'netgrav.dk',
  sv: 'natgrav.se',
  fi: 'verkkohauta.fi',
  en: 'nettgrav.com',
};

export const queries = {
  xs: '(min-width: 600px)', // Small screens and up
  sm: '(min-width: 960px)', // Medium screens and up
  md: '(min-width: 1280px)', // Large screens and up
  lg: '(min-width: 1440px)', // Extra-large screens and up
  xl: '(min-width: 1920px)', // XXL screens and up
  or: '(orientation: portrait)', // Portrait orientation
};

export const MOBILE_MAX = 768;

export const SubscriptionStatuses = {
  PENDING_APPROVAL: 'pending_approval',
  PAYMENT_DUE: 'payment_due',
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
  GRACE_PERIOD: 'grace_period',
};

export const ProductKeys = {
  free: 'free',
  conscious: 'conscious',
  conscious_plus: 'conscious-plus',
};

export const PaymentType = {
  vipps: 'vipps',
  card: 'card',
};

export const getMessageKey = (currentPlan, selectedPlan) => {
  switch (currentPlan.key) {
    case ProductKeys.free:
      return 'settings_subscribedPlan';
    case ProductKeys.conscious:
      if (selectedPlan.key === ProductKeys.conscious_plus) {
        return 'settings_updatePlan';
      }
      if (selectedPlan.key === currentPlan.key) {
        return 'settings_addedMember';
      }
      return 'settings_updatePlan';
    case ProductKeys.conscious_plus:
      if (selectedPlan.key === ProductKeys.conscious) {
        return 'settings_downgradePlan';
      }
      if (selectedPlan.key === currentPlan.key) {
        return 'settings_addedMember';
      }
      return 'settings_updatePlan';
    default:
      return 'settings_subscribedPlan';
  }
};

export const AboutMenuItems = {
  1: {
    title: 'Gravlagte',
  },
  2: {
    title: 'Relasjoner',
  },
  3: {
    title: 'Arbeid',
  },
  4: {
    title: 'Interesse',
  },
  5: {
    title: 'Lidenskap',
  },
  6: {
    title: 'Gravlenke',
  },
};

export const MemoryMenuItems = {
  1: {
    title: 'Gravtavlen',
  },
  2: {
    title: 'Merkedager',
  },
};

export const ChangePlanKey = {
  change_plan: 'change_plan',
  change_members: 'manage_members',
  change: 'change',
};

export const LanguageKeys = {
  English: 'ENG',
  Norwegian: 'NOR',
  Swedish: 'SWE',
  Danish: 'DAN',
  Finnish: 'FIN',
};

export const FIND_GRAVE_SLUGS = {
  [LanguageKeys.Norwegian]: 'finn-en-grav',
  [LanguageKeys.Swedish]: 'hitta-en-grav',
  [LanguageKeys.Danish]: 'find-en-grav',
  [LanguageKeys.Finnish]: 'etsi-hauta',
  [LanguageKeys.English]: 'find-a-grave',
};

export const FOOTER_PAGE_SLUGS = {
  about: {
    [LanguageKeys.Norwegian]: 'om-nettgrav',
    [LanguageKeys.Swedish]: 'om-natgrav',
    [LanguageKeys.Danish]: 'om-netgrav',
    [LanguageKeys.Finnish]: 'tietoa-verkkohaudasta',
    [LanguageKeys.English]: 'about-nettgrav',
  },
  createNettgrav: {
    [LanguageKeys.Norwegian]: 'opprette-en-nettgrav',
    [LanguageKeys.Swedish]: 'skapa-en-natgrav',
    [LanguageKeys.Danish]: 'opret-en-netgrav',
    [LanguageKeys.Finnish]: 'luo-verkkohauta',
    [LanguageKeys.English]: 'create-a-nettgrav',
  },
  createSpark: {
    [LanguageKeys.Norwegian]: 'skap-gnist',
    [LanguageKeys.Swedish]: 'skapa-gnista',
    [LanguageKeys.Danish]: 'skab-gnist',
    [LanguageKeys.Finnish]: 'luo-kipina',
    [LanguageKeys.English]: 'create-spark',
  },
  myLight: {
    [LanguageKeys.Norwegian]: 'mitt-lys',
    [LanguageKeys.Swedish]: 'mitt-ljus',
    [LanguageKeys.Danish]: 'mit-lys',
    [LanguageKeys.Finnish]: 'minun-valoni',
    [LanguageKeys.English]: 'my-light',
  },
  partner: {
    [LanguageKeys.Norwegian]: 'partner',
    [LanguageKeys.Swedish]: 'partner',
    [LanguageKeys.Danish]: 'partner',
    [LanguageKeys.Finnish]: 'kumppani',
    [LanguageKeys.English]: 'partner',
  },
  howItWorks: {
    [LanguageKeys.Norwegian]: 'slik-fungerer-nettgrav',
    [LanguageKeys.Swedish]: 'sa-fungerar-natgrav',
    [LanguageKeys.Danish]: 'sadan-fungerer-netgrav',
    [LanguageKeys.Finnish]: 'nain-verkkohauta-toimii',
    [LanguageKeys.English]: 'how-nettgrav-works',
  },
  endOfTimeCenter: {
    [LanguageKeys.Norwegian]: 'end-of-time-center',
    [LanguageKeys.Swedish]: 'end-of-time-center',
    [LanguageKeys.Danish]: 'end-of-time-center',
    [LanguageKeys.Finnish]: 'end-of-time-center',
    [LanguageKeys.English]: 'end-of-time-center',
  },
  endOfTimeChat: {
    [LanguageKeys.Norwegian]: 'end-of-time-chat',
    [LanguageKeys.Swedish]: 'end-of-time-chat',
    [LanguageKeys.Danish]: 'end-of-time-chat',
    [LanguageKeys.Finnish]: 'end-of-time-chat',
    [LanguageKeys.English]: 'end-of-time-chat',
  },
};

export const ROOM_PATH_SEGMENTS = {
  [LanguageKeys.Norwegian]: { base: 'rom', create: 'nytt', accept: 'bli-med' },
  [LanguageKeys.Swedish]: { base: 'rum', create: 'ny', accept: 'ga-med' },
  [LanguageKeys.Danish]: { base: 'rum', create: 'nyt', accept: 'bliv-med' },
  [LanguageKeys.Finnish]: { base: 'huoneet', create: 'uusi', accept: 'liity' },
  [LanguageKeys.English]: { base: 'rooms', create: 'new', accept: 'accept' },
};

export const ROOM_TAB_SEGMENTS = {
  [LanguageKeys.Norwegian]: {
    about: 'om',
    legacy_discussions: 'samtaler',
    members_chat: 'medlemschat',
    members: 'medlemmer',
    files: 'filer',
    requests: 'foresporsler',
    settings: 'innstillinger',
  },
  [LanguageKeys.Swedish]: {
    about: 'om',
    legacy_discussions: 'samtal',
    members_chat: 'medlemschatt',
    members: 'medlemmar',
    files: 'filer',
    requests: 'forfragningar',
    settings: 'installningar',
  },
  [LanguageKeys.Danish]: {
    about: 'om',
    legacy_discussions: 'samtaler',
    members_chat: 'medlemschat',
    members: 'medlemmer',
    files: 'filer',
    requests: 'foresporgsler',
    settings: 'indstillinger',
  },
  [LanguageKeys.Finnish]: {
    about: 'tietoa',
    legacy_discussions: 'keskustelut',
    members_chat: 'jasenchatti',
    members: 'jasenet',
    files: 'tiedostot',
    requests: 'pyynnot',
    settings: 'asetukset',
  },
  [LanguageKeys.English]: {
    about: 'about',
    legacy_discussions: 'discussions',
    members_chat: 'members-chat',
    members: 'members',
    files: 'files',
    requests: 'requests',
    settings: 'settings',
  },
};

export const getFindGraveSlug = () => {
  const lang = getDefaultLanguage();
  return FIND_GRAVE_SLUGS[lang] || FIND_GRAVE_SLUGS[LanguageKeys.Norwegian];
};

export const getFindGravePath = () => `/${getFindGraveSlug()}`;

export const getFooterPagePath = (pageKey) => {
  const lang = getDefaultLanguage();
  const slugs = FOOTER_PAGE_SLUGS[pageKey];
  if (!slugs) return '/';
  const slug = slugs[lang] || slugs[LanguageKeys.Norwegian];
  return `/${slug}`;
};

export const getRoomPathSegments = (language = getDefaultLanguage()) => {
  const lang = language;
  return ROOM_PATH_SEGMENTS[lang] || ROOM_PATH_SEGMENTS[LanguageKeys.Norwegian];
};

export const getRoomTabSegments = (language = getDefaultLanguage()) => {
  const lang = language;
  return ROOM_TAB_SEGMENTS[lang] || ROOM_TAB_SEGMENTS[LanguageKeys.Norwegian];
};

export const getRoomTabRoute = (tabKey) => {
  const segments = getRoomTabSegments();
  return segments[tabKey] || segments.members_chat;
};

export const getRoomTabPath = (id, tabKey) =>
  `${getRoomDetailsPath(id)}/${getRoomTabRoute(tabKey)}`;

export const getRoomsBasePath = () => {
  const { base } = getRoomPathSegments();
  return `/${base}`;
};

export const getRoomsCreatePath = () => {
  const { base, create } = getRoomPathSegments();
  return `/${base}/${create}`;
};

export const getRoomsAcceptPath = () => {
  const { base, accept } = getRoomPathSegments();
  return `/${base}/${accept}`;
};

export const getRoomDetailsPath = (id) => `${getRoomsBasePath()}/${id}`;

export const isRoomsPath = (pathname = '') => {
  const base = getRoomsBasePath();
  return pathname === base || pathname.startsWith(`${base}/`) || pathname.startsWith('/groups');
};

export const getLanguageForHostname = (hostname = '') => {
  const host = String(hostname || '').toLowerCase();
  if (host.includes('nettgrav.com')) return LanguageKeys.English;
  if (host.includes('natgrav.se')) return LanguageKeys.Swedish;
  if (host.includes('netgrav.dk')) return LanguageKeys.Danish;
  if (host.includes('verkkohauta.fi')) return LanguageKeys.Finnish;
  return LanguageKeys.Norwegian;
};

export const getDefaultLanguage = () =>
  getLanguageForHostname(typeof window === 'undefined' ? '' : window.location?.hostname);

export const languages = [
  {
    id: 1,
    key: 'ENG',
    name: 'English',
    native_name: 'English',
  },
  {
    id: 2,
    key: 'NOR',
    name: 'Norwegian',
    native_name: 'Norsk',
  },
  {
    id: 3,
    key: 'SWE',
    name: 'Swedish',
    native_name: 'Svenska',
  },
  {
    id: 4,
    key: 'DAN',
    name: 'Danish',
    native_name: 'Dansk',
  },
  {
    id: 5,
    key: 'FIN',
    name: 'Finnish',
    native_name: 'Suomi',
  },
];

export const SubscriptionAlertsKeys = {
  paymentDue: 'paymentDue',
  active: 'active',
  pending: 'pending',
  stopped: 'stopped',
  expired: 'expired',
  cancelled: 'cancelled',
  activeStopped: 'activeStopped',
  activeExpired: 'activeExpired',
  notValid: 'notValid',
};

export const SubscriptionChangesKey = {
  period: 'period',
  user_count: 'user_count',
  product_id: 'product_id',
};

export const SubscriptionPeriodKey = {
  monthly: 'monthly',
  semi_annual: 'semi_annual',
  annually: 'annually',
  biennial: 'biennial',
};

export const getFrontendBaseUrl = () => {
  const host = window?.location?.hostname || '';
  if (host.includes('nettgrav.com')) return 'https://nettgrav.com';
  if (host.includes('natgrav.se')) return 'https://natgrav.se';
  if (host.includes('netgrav.dk')) return 'https://netgrav.dk';
  if (host.includes('verkkohauta.fi')) return 'https://verkkohauta.fi';
  switch (getEnv('REACT_APP_ENV')) {
    case 'staging':
      return 'https://stage.nettgrav.no';
    case 'production':
      return 'https://nettgrav.no';
    case 'local':
      return 'http://localhost:5173';
    default:
      return 'https://stage.nettgrav.no';
  }
};

const HOST_SEO_DEFAULTS = [
  {
    test: (host) => host.includes('nettgrav.com'),
    siteName: 'Nettgrav.com',
    description:
      'Are you looking to find or create a meaningful grave? Nettgrav.com is a private and personal network that lets modern families continue their love for the deceased with photos, films, words, and music. Find or create a virtual grave here – Nettgrav.com.',
  },
  {
    test: (host) => host.includes('natgrav.se'),
    siteName: 'Natgrav.se',
    description:
      'Letar du efter en grav eller vill skapa en minnessida? Natgrav.se är ett privat nätverk där familjer kan hedra sina avlidna med bilder, filmer, ord och musik. Hitta eller skapa en digital grav här – Natgrav.se.',
  },
  {
    test: (host) => host.includes('netgrav.dk'),
    siteName: 'Netgrav.dk',
    description:
      'Leder du efter en grav eller vil du skabe et minde? Netgrav.dk er et privat netværk hvor familier kan ære deres afdøde med billeder, film, ord og musik. Find eller opret en digital grav her – Netgrav.dk.',
  },
  {
    test: (host) => host.includes('verkkohauta.fi'),
    siteName: 'Verkkohauta.fi',
    description:
      'Etsitkö hautaa tai haluatko luoda muistosivun? Verkkohauta.fi on yksityinen verkosto, jossa perheet voivat vaalia vainajia kuvien, videoiden, sanojen ja musiikin avulla. Löydä tai luo digitaalinen hauta täällä – Verkkohauta.fi.',
  },
  {
    test: (host) => host.includes('nettgrav.no'),
    siteName: 'Nettgrav.no',
    description:
      'Leter du etter å finne eller opprette en meningsfull grav? Nettgrav.no er et privat og personlig nettverk som lar familier holde minnene levende med bilder, filmer, ord og musikk. Finn eller opprett en virtuell grav her – Nettgrav.no.',
  },
];

const getHostSeoDefaults = () => {
  const host = window?.location?.hostname || '';
  return (
    HOST_SEO_DEFAULTS.find((entry) => entry.test(host)) || {
      siteName: 'Nettgrav.no',
      description:
        'Are you looking to find or create a meaningful grave? Nettgrav.no is a private and personal network that gives modern families the opportunity to continue the love they have for deceased with pictures, films, words and music. Find or create a virtual grave here - Nettgrav.no.',
    }
  );
};

export const getSiteNameForHost = () => getHostSeoDefaults().siteName;

export const getDefaultSeoDescription = () => getHostSeoDefaults().description;

export const getMemoryShareUrl = (graveSlug, memoryId) => {
  const baseUrl = getFrontendBaseUrl();
  return `${baseUrl}/graves/${graveSlug}?memory=${memoryId}`;
};

export const getLifeEventShareUrl = (graveSlug, eventId) => {
  const baseUrl = getFrontendBaseUrl();
  return `${baseUrl}/graves/${graveSlug}?tab=events&event_id=${eventId}`;
};

export const getSparkShareUrl = (userId, sparkId) => {
  const baseUrl = getFrontendBaseUrl();
  return `${baseUrl}/members/${userId}?spark=${sparkId}`;
};

export const getCardIcon = (brand = '') => {
  const b = brand?.toLowerCase() || '';
  const map = {
    visa: visaLogo,
    mastercard: mastercardLogo,
    paypal: paypalLogo,
    alipay: alipayLogo,
    amex: amexLogo,
    'american express': amexLogo,
    elo: eloLogo,
    hiper: hiperLogo,
    hipercard: hipercardLogo,
    jcb: jcbLogo,
    maestro: maestroLogo,
    mir: mirLogo,
    unionpay: unionpayLogo,
  };
  const logo = map[b];
  if (logo) return <img src={logo} alt={brand} style={{ width: '100%', height: 'auto' }} />;
  return <FaRegCreditCard />;
};
